import { AxiosResponse } from 'axios';
import ExcelJS from 'exceljs';

import { ResolvedBackfillRecord } from './CertBackfillResolve';
import { WorkdayEmployeeCertification } from '../types/Workday/WorkdayEmployeeCertification';
import { certifyEmployee } from '../services/Workday/WorkdayCertificationService';

/** Number of certifyEmployee writes issued concurrently per batch. Tune here. */
export const BATCH_SIZE = 10;

/**
 * Runtime status of a single Ready row through the backfill import lifecycle:
 * - `checking` — the initial DB existence check is still loading
 * - `exists`   — already present in the DB before this run (skipped, never re-POSTed)
 * - `queued`   — not in the DB and not yet run (eligible to import)
 * - `running`  — a certifyEmployee write is in flight for this row
 * - `success`  — written this run (HTTP 2xx) or already existed (HTTP 409)
 * - `error`    — the write failed
 */
export type BackfillRowStatus = 'checking' | 'exists' | 'queued' | 'running' | 'success' | 'error';

/** Outcome of one certifyEmployee call, kept per row for status display and the results export. */
export interface BackfillRunResult {
    status: Extract<BackfillRowStatus, 'success' | 'error'>;
    httpStatus: number;   // 0 when the request never reached the server
    message: string;
}

/** Statuses whose row can be run individually or picked up by the batch runner. */
export const RUNNABLE_STATUSES: ReadonlySet<BackfillRowStatus> = new Set<BackfillRowStatus>(['queued', 'error']);

/** Human-readable label for each status, used in the results export. */
export const OUTCOME_LABEL: Readonly<Record<BackfillRowStatus, string>> = {
    checking: 'Checking',
    exists: 'Already in database',
    queued: 'Not run',
    running: 'In progress',
    success: 'Imported',
    error: 'Error',
};

/** Stable per-row identity from a row number + cert name (one kept (employee, cert) pair per source row). */
export const rowKeyOf = (rowNumber: number, certName: string): string => `${rowNumber}|${certName}`;

/** Stable per-row identity for a resolved record. */
export const rowKey = (rec: ResolvedBackfillRecord): string => rowKeyOf(rec.source.rowNumber, rec.source.certName);

/**
 * The (employee, certification) identity used to decide whether a cert is already in the DB.
 *
 * This MUST mirror the rule certifyEmployee dedupes on, which is (EmployeeId, WorkdayCertificationId)
 * alone — it does not consider dates and does not update them on conflict. Including expiration here
 * made the two disagree: a cert already present with a different expiration missed the pre-check, showed
 * as "To import", could only ever 409 on POST, and was then flipped to a false error by the reconcile
 * pass looking for a key that could not exist. Since dedupeKeepLatestExpiration deliberately keeps the
 * newest expiration, "same employee and cert, newer date" is the ordinary case on a re-export, not an edge.
 *
 * Consequence worth knowing: a row whose cert exists with a stale expiration now reports as already in
 * the DB and is not re-sent — which is accurate, because the backend would refuse to update it anyway.
 */
export const existenceKey = (employeeId: number, certificationId: number): string =>
    `${employeeId}|${certificationId}`;

/**
 * Builds the set of (employee, cert) keys already present in the DB.
 *
 * Note the backend also excludes rows whose SyncOperation is 'Delete' from its duplicate check, so a cert
 * pending deletion is re-certifiable there but counts as existing here. The list endpoint does not return
 * SyncOperation, so that clause cannot be mirrored; the effect is a row being skipped rather than
 * wrongly written, which is the safer direction to be wrong in.
 */
export const buildExistingCertKeys = (employeeCerts: WorkdayEmployeeCertification[]): Set<string> => {
    const keys = new Set<string>();
    for (const ec of employeeCerts) {
        const employeeId = Number(ec.employee?.employeeID);
        if (!Number.isFinite(employeeId) || !ec.certification) continue;
        keys.add(existenceKey(employeeId, ec.certification.id));
    }
    return keys;
};

/** The (employee, cert) key for a resolved record; null when the required ids are missing. */
export const recordExistenceKey = (rec: ResolvedBackfillRecord): string | null =>
    rec.employeeId !== null && rec.certificationId !== null
        ? existenceKey(rec.employeeId, rec.certificationId)
        : null;

/** Best-effort human-readable message from a certifyEmployee error response body. */
const extractResponseMessage = (response: AxiosResponse): string | null => {
    const data = response?.data as unknown;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
        const obj = data as Record<string, unknown>;
        const candidate = obj.message ?? obj.detail ?? obj.title;
        if (typeof candidate === 'string' && candidate.trim()) return candidate;
    }
    return null;
};

/**
 * Classifies a certifyEmployee response. Because this stage only writes to the DB (Boomi syncs to
 * Workday later), a 2xx means the row is in the DB and a 409 means it was already there — both are
 * treated as success (green). Anything else, including a status of 0 (no response), is an error.
 */
export const classifyCertifyResponse = (response: AxiosResponse): BackfillRunResult => {
    const httpStatus = response?.status ?? 0;
    if (httpStatus >= 200 && httpStatus < 300) return { status: 'success', httpStatus, message: 'Created' };
    if (httpStatus === 409) return { status: 'success', httpStatus, message: 'Already in database' };
    const message = extractResponseMessage(response) ?? (httpStatus ? `HTTP ${httpStatus}` : 'Request failed (no response)');
    return { status: 'error', httpStatus, message };
};

/**
 * Writes one resolved record via certifyEmployee and classifies the result. This is the single
 * primitive shared by the batch runner and a per-row manual run. Records missing a required id are
 * reported as an error rather than sent.
 */
export const runOne = async (rec: ResolvedBackfillRecord, userEmail: string): Promise<BackfillRunResult> => {
    if (rec.employeeId === null || rec.certificationId === null || rec.certifiedById === null) {
        return { status: 'error', httpStatus: 0, message: 'Missing a required id (row is not ready)' };
    }
    const response = await certifyEmployee(
        rec.employeeId,
        rec.certificationId,
        rec.source.issuedDate,
        rec.source.expirationDate,
        rec.certifiedById,
        userEmail,
    );
    return classifyCertifyResponse(response);
};

/** One row of the results export, pairing a resolved record with its final status. */
export interface BackfillResultRow {
    rec: ResolvedBackfillRecord;
    status: BackfillRowStatus;
    httpStatus?: number;
    message?: string;
}

/**
 * Builds and downloads an .xlsx audit of the run — one row per Ready record with the exact ids that
 * were POSTed and the outcome. Follows the Blob-download pattern used by ExportToExcelButton.
 */
export const exportBackfillResultsXlsx = async (rows: BackfillResultRow[], fileName: string): Promise<void> => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Backfill Results');

    worksheet.addRow([
        'Row', 'Emp No.', 'Trainer', 'Category', 'Certification', 'Certified By',
        'employeeId', 'certificationId', 'certifiedById', 'Issued', 'Expires',
        'Outcome', 'HTTP Status', 'Message',
    ]);

    for (const { rec, status, httpStatus, message } of rows) {
        const s = rec.source;
        worksheet.addRow([
            s.rowNumber, s.empNo, s.trainerName, s.category, s.certName, s.certifiedByName,
            rec.employeeId, rec.certificationId, rec.certifiedById, s.issuedDate, s.expirationDate,
            OUTCOME_LABEL[status], httpStatus ?? '', message ?? '',
        ]);
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
};
